import React from 'react';
import { Clock, MapPin, CheckCircle, Users, ArrowRight, Edit2, AlertTriangle, Sparkles, ChevronDown, Home } from 'lucide-react';
interface Property {
  id: number;
  address: string;
  mq: number;
  rooms: number;
  bathrooms: number;
  timeWindow: string;
  tags: string[];
}
interface PackageData {
  id: number;
  name: string;
  zoneName: string;
  count: number;
  properties: Property[];
  baseEarnings: number;
  status: 'available' | 'accepted' | 'assigned' | 'completed';
  completedDate?: string;
  groupingReason?: string;
  groupingTags?: string[];
}
interface PackageCardProps {
  pkg: PackageData;
  isActive: boolean;
  onSelect: (id: number) => void;
  onEdit?: () => void;
  onAccept?: (id: number) => void;
}
export function PackageCard({
  pkg,
  isActive,
  onSelect,
  onEdit,
  onAccept
}: PackageCardProps) {
  const totalEarnings = pkg.properties.length * 45 + (pkg.properties.length > 2 ? 20 : 0);
  const totalTime = pkg.properties.length * 1.5;
  const isTightSchedule = totalTime > 5;
  const isCompleted = pkg.status === 'completed';
  const isAssigned = pkg.status === 'assigned';
  const isAccepted = pkg.status === 'accepted';
  // Time window range
  const timeWindows = pkg.properties.map(p => p.timeWindow);
  const allStarts = timeWindows.map(tw => tw.split(' - ')[0]).sort();
  const allEnds = timeWindows.map(tw => tw.split(' - ')[1]).sort();
  const timeRange = allStarts.length > 0 ? `${allStarts[0]} – ${allEnds[allEnds.length - 1]}` : '';
  return <div onClick={() => onSelect(pkg.id)} className={`relative bg-white border rounded-xl cursor-pointer transition-all duration-300 ease-in-out ${isActive ? 'border-teal-deep/40 shadow-lg ring-1 ring-teal-deep/20' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'}`} data-id="element-1369">
      {/* Compact Header — always visible */}
      <div className="p-3.5" data-id="element-1370">
        <div className="flex items-center justify-between" data-id="element-1371">
          <div className="flex items-center gap-2.5 min-w-0" data-id="element-1372">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-teal-deep/10' : 'bg-gray-100'}`} data-id="element-1373">
              <Home size={14} className={isActive ? 'text-teal-deep' : 'text-gray-500'} data-id="element-1374" />
            </div>
            <div className="min-w-0" data-id="element-1375">
              <div className="flex items-center gap-1.5" data-id="element-1376">
                <h3 className="font-semibold text-navy text-sm truncate" data-id="element-1377">
                  {pkg.name}
                </h3>
                {isCompleted && <CheckCircle size={12} className="text-green-500 flex-shrink-0" data-id="element-1378" />}
              </div>
              <div className="flex items-center gap-2 mt-0.5" data-id="element-1379">
                <span className="text-[11px] text-gray-400" data-id="element-1380">
                  {pkg.count} proprietà
                </span>
                {timeRange && !isCompleted && <>
                    <span className="w-0.5 h-0.5 bg-gray-300 rounded-full" data-id="element-1381" />
                    <span className="text-[11px] text-gray-400" data-id="element-1382">
                      {timeRange}
                    </span>
                  </>}
              </div>
            </div>
          </div>
          <div className="text-right flex-shrink-0 ml-3" data-id="element-1383">
            <div className="font-bold text-base" style={{
            color: '#2E8C7E'
          }} data-id="element-1384">
              €{totalEarnings}
            </div>
            <div className="text-[10px] text-gray-400" data-id="element-1385">{totalTime}h</div>
          </div>
        </div>

        {/* Grouping tags — compact benefit pills, always visible */}
        {pkg.groupingTags && pkg.groupingTags.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2.5" data-id="element-1386">
            {pkg.groupingTags.slice(0, isActive ? undefined : 3).map(tag => <span key={tag} className="px-2 py-0.5 bg-gray-50 text-gray-600 rounded-full text-[10px] font-medium border border-gray-100" data-id="element-1387">
                {tag}
              </span>)}
          </div>}
      </div>

      {/* Expanded Content — only when active */}
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isActive ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1388">
        <div className="px-3.5 pb-3.5 space-y-3" data-id="element-1389">
          {/* Divider */}
          <div className="border-t border-gray-100" data-id="element-1390" />

          {/* AI Grouping Reason */}
          {pkg.groupingReason && <div className="flex items-start gap-2 bg-blue-50/60 rounded-lg p-2.5" data-id="element-1391">
              <Sparkles size={12} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-1392" />
              <p className="text-[11px] text-blue-700 leading-relaxed" data-id="element-1393">
                {pkg.groupingReason}
              </p>
            </div>}

          {/* Property List */}
          <div className="space-y-1.5" data-id="element-1394">
            {pkg.properties.map(prop => <div key={prop.id} className="flex items-center justify-between text-xs py-1" data-id="element-1395">
                <div className="flex items-center gap-2 text-gray-600 min-w-0 flex-1" data-id="element-1396">
                  <MapPin size={10} className="text-gray-400 flex-shrink-0" data-id="element-1397" />
                  <span className="truncate" data-id="element-1398">{prop.address}</span>
                </div>
                <span className="text-[10px] text-gray-400 ml-2 flex-shrink-0" data-id="element-1399">
                  {prop.mq}mq · {prop.timeWindow}
                </span>
              </div>)}
          </div>

          {/* Warning */}
          {isTightSchedule && !isCompleted && <div className="flex items-center gap-1.5 text-[11px] text-amber-600 bg-amber-50 px-2.5 py-1.5 rounded-lg border border-amber-100" data-id="element-1400">
              <AlertTriangle size={11} data-id="element-1401" />
              <span data-id="element-1402">Finestra temporale stretta</span>
            </div>}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2" data-id="element-1403">
            {isCompleted ? <span className="text-[11px] font-medium text-green-600 flex items-center gap-1" data-id="element-1404">
                <CheckCircle size={12} data-id="element-1405" />
                Completato il {pkg.completedDate}
              </span> : isAssigned ? <div className="flex items-center gap-2" data-id="element-1406">
                <div className="flex -space-x-1.5" data-id="element-1407">
                  <div className="w-5 h-5 rounded-full bg-pink-100 border-2 border-white flex items-center justify-center text-[8px] font-bold text-pink-700" data-id="element-1408">
                    LM
                  </div>
                  <div className="w-5 h-5 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center text-[8px] font-bold text-blue-700" data-id="element-1409">
                    MR
                  </div>
                </div>
                <span className="text-[11px] text-gray-500" data-id="element-1410">Assegnato</span>
              </div> : isAccepted ? <button onClick={e => {
            e.stopPropagation();
            onAccept?.(pkg.id);
          }} className="text-xs font-medium text-white bg-teal-deep hover:bg-teal-deep/90 px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5" data-id="element-1411">
                <Users size={12} data-id="element-1412" />
                Assegna Team
              </button> : <button onClick={e => {
            e.stopPropagation();
            onAccept?.(pkg.id);
          }} className="text-xs font-medium text-white bg-teal-deep hover:bg-teal-deep/90 px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5" data-id="element-1413">
                Accetta
                <ArrowRight size={12} data-id="element-1414" />
              </button>}

            {!isCompleted && !isAssigned && onEdit && <button onClick={e => {
            e.stopPropagation();
            onEdit();
          }} className="p-1.5 text-gray-400 hover:text-navy hover:bg-gray-100 rounded-lg transition-colors" title="Modifica pacchetto" data-id="element-1415">
                <Edit2 size={13} data-id="element-1416" />
              </button>}
          </div>
        </div>
      </div>
    </div>;
}